import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { LayoutTemplate } from 'lucide-react';
import API from '../../utils/constant';
import { Button, ConfirmDialog } from '../../school_panel/components/ui';

export type Blueprint = {
  available: boolean;
  applied?: boolean;
  grades?: { id: number; name: string; subjects: string[] }[];
  totals?: { grades: number; subjects: number; chapters: number };
};
type Added = { grades: number; subjects: number; chapters: number };

const errDetail = (err: any, fallback: string) =>
  (typeof err?.response?.data?.detail === 'string' && err.response.data.detail) || fallback;

// Everything that lists the school's curriculum refetches after a change.
export function useInvalidateCurriculum() {
  const qc = useQueryClient();
  return () => {
    for (const key of ['school-setup-summary', 'school-blueprint']) qc.invalidateQueries({ queryKey: [key] });
    // Legacy setup hooks (react-query/queries) use these keys.
    qc.invalidateQueries({ predicate: (q) => /grade|subject|chapter/i.test(String(q.queryKey[0])) });
  };
}

/**
 * "Start from the YADA blueprint": links the eltest-school grades, subjects and chapters
 * into this school in one go. Shown only while the school has no grades; afterwards the
 * admin removes what they don't teach and adds their own.
 */
export function BlueprintCard({ onApplied, compact = false }: { onApplied?: (added: Added) => void; compact?: boolean }) {
  const q = useQuery({
    queryKey: ['school-blueprint'],
    queryFn: () => API.get<Blueprint>('/api/school/setup/blueprint/').then((r) => r.data),
  });
  const invalidate = useInvalidateCurriculum();
  const [dismissed, setDismissed] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const apply = useMutation({
    mutationFn: () => API.post<{ added: Added }>('/api/school/setup/blueprint/').then((r) => r.data.added),
    onSuccess: (added) => { setConfirming(false); invalidate(); onApplied?.(added); },
  });
  const bp = q.data;
  if (dismissed || !bp?.available || bp.applied || !bp.totals) return null;
  const t = bp.totals;
  return (
    <section aria-labelledby="bp-title" className="rounded-[18px] border border-accent-line bg-accent-bg p-5">
      <div className="flex flex-wrap items-start gap-4">
        <span aria-hidden className="grid h-11 w-11 flex-none place-items-center rounded-[13px] bg-navy text-white"><LayoutTemplate size={20} /></span>
        <div className="min-w-0 flex-[1_1_320px]">
          <h2 id="bp-title" className="font-display text-[17px] font-bold text-warm-ink">Start from the YADA blueprint</h2>
          <p className="mt-1 text-[13px] text-warm-ink/85">
            The standard Ethiopian high-school setup in one step: {t.grades} grades, {t.subjects} subjects and {t.chapters} chapters.
            Afterwards, remove anything you don't teach and add your own.
          </p>
          {!compact && bp.grades && (
            <ul className="mt-3 flex flex-wrap gap-2" aria-label="Blueprint grades">
              {bp.grades.map((g) => (
                <li key={g.id} title={g.subjects.join(', ')}
                  className="rounded-full bg-surface px-3 py-1 text-[13px] text-ink">
                  Grade {g.name} <span className="font-mono text-xs text-muted">· {g.subjects.length} subjects</span>
                </li>
              ))}
            </ul>
          )}
          {apply.isError && <p role="alert" className="mt-2 text-sm text-danger-ink">{errDetail(apply.error, "Couldn't apply the blueprint. Try again.")}</p>}
        </div>
        <div className="flex flex-wrap gap-2">
          <Button onClick={() => setConfirming(true)} loading={apply.isPending}>Use the blueprint</Button>
          <Button variant="ghost" onClick={() => setDismissed(true)}>Start from scratch</Button>
        </div>
      </div>
      <ConfirmDialog isOpen={confirming} danger={false} busy={apply.isPending}
        title="Use the YADA blueprint?"
        body={`Your school gets ${t.grades} grades, ${t.subjects} subjects and ${t.chapters} chapters. Nothing you added yourself changes, and you can remove any of it later.`}
        confirmLabel="Use the blueprint" onConfirm={() => apply.mutate()} onCancel={() => setConfirming(false)} />
    </section>
  );
}

export type RemoveTarget =
  | { kind: 'grade'; gradeId: number; name: string }
  | { kind: 'subject'; gradeId: number; subjectId: number; name: string; gradeName: string }
  | { kind: 'chapter'; chapterId: number; name: string };

const URL: Record<RemoveTarget['kind'], (t: any) => string> = {
  grade: (t) => `/api/school/setup/grades/${t.gradeId}/`,
  subject: (t) => `/api/school/setup/grades/${t.gradeId}/subjects/${t.subjectId}/`,
  chapter: (t) => `/api/school/setup/chapters/${t.chapterId}/`,
};

// `name` / `gradeName` are display labels such as "Grade 9".
const COPY = (t: RemoveTarget) => (t.kind === 'grade'
  ? { title: `Remove ${t.name}?`, body: `Your school stops teaching ${t.name}: its subjects, chapters and teacher assignments are removed from your school. Other schools are not affected, and you can add it back later. A grade that still has students, sections or exams can't be removed.` }
  : t.kind === 'subject'
    ? { title: `Remove ${t.name} from ${t.gradeName}?`, body: `Its chapters and teacher assignments in ${t.gradeName} are removed from your school. You can add it back later.` }
    : { title: `Remove "${t.name}"?`, body: 'This chapter is removed from your school only. You can add it back from the preset list later.' });

/** Confirm-then-unlink for a grade, a subject in a grade, or a chapter. The server refuses (409) while students, sections, exams or tasks use it. */
export function RemoveDialog({ target, onClose, onRemoved }: { target: RemoveTarget | null; onClose: () => void; onRemoved?: (t: RemoveTarget) => void }) {
  const invalidate = useInvalidateCurriculum();
  const remove = useMutation({
    mutationFn: (t: RemoveTarget) => API.delete(URL[t.kind](t)),
    onSuccess: (_d, t) => { invalidate(); onRemoved?.(t); onClose(); },
  });
  const copy = target ? COPY(target) : null;
  const close = () => { remove.reset(); onClose(); };
  return (
    <ConfirmDialog isOpen={target !== null} title={copy?.title ?? ''} busy={remove.isPending} confirmLabel="Remove"
      body={(
        <>
          <span className="block">{copy?.body}</span>
          {remove.isError && <span role="alert" className="mt-3 block rounded-[12px] bg-danger-bg px-3 py-2 text-danger-ink">{errDetail(remove.error, "Couldn't remove it. Try again.")}</span>}
        </>
      )}
      onConfirm={() => target && remove.mutate(target)} onCancel={close} />
  );
}
